import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiHome,
  FiUsers,
  FiShoppingBag,
  FiBarChart2,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiUser,
  FiPackage,
  FiCoffee,
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const AdminLayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: #f8f9fa;
  position: relative;

  @media (min-width: 769px) {
    min-height: calc(100vh - 80px);
    margin-top: 80px;
  }
`;

const Sidebar = styled(motion.div)`
  width: 280px;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  height: 100vh;
  top: 0;
  left: ${(props) => (props.$isOpen ? "0" : "-280px")};
  transition: left 0.3s ease;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: 769px) {
    height: calc(100vh - 80px);
    top: 80px;
    left: 0 !important;
  }

  @media (max-height: 700px) {
    /* On shorter screens, make sidebar scrollable */
    overflow-y: auto;
  }

  @media (max-width: 1024px) and (max-height: 800px) {
    /* On smaller desktop screens with limited height */
    overflow-y: auto;
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  position: relative;

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }

  .close-button {
    display: none;
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #666;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      color: #2d5016;
    }

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const SidebarMenu = styled.nav`
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  min-height: 0; /* Allow flex item to shrink below content size */

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: #666;
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;

  &:hover {
    background: #f8f9fa;
    color: #2d5016;
  }

  &.active {
    background: #f0f8f0;
    color: #2d5016;
    border-left-color: #2d5016;
    font-weight: 600;
  }

  .icon {
    font-size: 1.25rem;
  }
`;

const SidebarFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0; /* Prevent footer from shrinking */
  background: white; /* Ensure footer has solid background */

  .user-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;

    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #2d5016, #4a7c59);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
    }

    .user-details {
      flex: 1;

      .user-name {
        font-weight: 600;
        color: #2d5016;
        margin-bottom: 0.25rem;
      }

      .user-role {
        color: #666;
        font-size: 0.875rem;
      }
    }
  }

  .logout-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e0e0e0;
    background: white;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #dc3545;
      color: #dc3545;
    }
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: 769px) {
    margin-left: 280px;
    width: calc(100% - 280px);
  }
`;

const MobileMenuButton = styled.button`
  position: fixed;
  top: 1rem;
  left: 1rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #2d5016;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  z-index: 1000;
  transition: all 0.3s ease;

  &:hover {
    background: #1a3a0e;
    transform: scale(1.05);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const TopBar = styled.div`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;

  @media (min-width: 769px) {
    top: 80px;
  }

  .top-bar-actions {
    display: flex;
    align-items: center;
    gap: 1rem;

    .notification-button {
      position: relative;
      background: none;
      border: none;
      font-size: 1.25rem;
      color: #666;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 50%;
      transition: all 0.3s ease;

      &:hover {
        background: #f8f9fa;
        color: #2d5016;
      }

      .notification-badge {
        position: absolute;
        top: 0;
        right: 0;
        width: 8px;
        height: 8px;
        background: #dc3545;
        border-radius: 50%;
      }
    }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 0;
  position: relative;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  cursor: pointer;

  @media (min-width: 769px) {
    display: none;
  }
`;

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Debug: Log sidebar state changes
  useEffect(() => {
    console.log("=== ADMIN SIDEBAR STATE ===");
    console.log("sidebarOpen:", sidebarOpen);
    console.log("isDesktop:", isDesktop);
  }, [sidebarOpen, isDesktop]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 769);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const menuItems = [
    {
      path: "/admin",
      label: "Dashboard",
      icon: <FiHome />,
    },
    {
      path: "/admin/vendors",
      label: "Vendor Management",
      icon: <FiUsers />,
    },
    {
      path: "/admin/meal-plans",
      label: "Meal Plan Management",
      icon: <FiPackage />,
    },
    {
      path: "/admin/meals",
      label: "Meal Management",
      icon: <FiCoffee />,
    },
    {
      path: "/admin/employees",
      label: "Employee Management",
      icon: <FiUser />,
    },
    {
      path: "/admin/orders",
      label: "Order Management",
      icon: <FiShoppingBag />,
    },
    {
      path: "/admin/notifications",
      label: "Notifications",
      icon: <FiBell />,
    },
    {
      path: "/admin/analytics",
      label: "Analytics",
      icon: <FiBarChart2 />,
    },
    {
      path: "/admin/settings",
      label: "Settings",
      icon: <FiSettings />,
    },
  ];

  const handleLogout = () => {
    logout();
  };

  const closeSidebar = () => {
    console.log("Close sidebar called - current state:", sidebarOpen);
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    console.log("Toggle sidebar - current state:", sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <AdminLayoutContainer>
      {sidebarOpen && <Overlay onClick={closeSidebar} />}

      <Sidebar
        $isOpen={sidebarOpen}
        initial={{ x: -280 }}
        animate={{
          x: isDesktop ? 0 : sidebarOpen ? 0 : -280,
        }}
        transition={{ duration: 0.3 }}
      >
        <SidebarHeader>
          <h1>NourishGY Admin</h1>
          <p>Platform Management</p>
          <button className="close-button" onClick={closeSidebar}>
            <FiX />
          </button>
        </SidebarHeader>

        <SidebarMenu>
          {menuItems.map((item) => (
            <MenuItem
              key={item.path}
              to={item.path}
              className={
                location.pathname === item.path ||
                (item.path !== "/admin" &&
                  location.pathname.startsWith(item.path))
                  ? "active"
                  : ""
              }
              onClick={closeSidebar}
            >
              <span className="icon">{item.icon}</span>
              {item.label}
            </MenuItem>
          ))}
        </SidebarMenu>

        <SidebarFooter>
          <div className="user-info">
            <div className="user-avatar">
              {user?.firstName?.charAt(0) || "A"}
            </div>
            <div className="user-details">
              <div className="user-name">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="user-role">Administrator</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <FiLogOut />
            Logout
          </button>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        <MobileMenuButton onClick={toggleSidebar}>
          <FiMenu />
        </MobileMenuButton>
        <TopBar>
          <div className="top-bar-actions">
            <button className="notification-button">
              <FiBell />
              <span className="notification-badge"></span>
            </button>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FiUser />
              <span style={{ color: "#666", fontSize: "0.875rem" }}>
                {user?.firstName} {user?.lastName}
              </span>
            </div>
          </div>
        </TopBar>

        <ContentArea>{children}</ContentArea>
      </MainContent>
    </AdminLayoutContainer>
  );
};

export default AdminLayout;
